import React, { useState, useEffect } from 'react';
import { QRCode } from 'react-qr-code';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack, 
  Divider,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QrCodeIcon from '@mui/icons-material/QrCode';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export default function KioskLauncher() {
  const [kiosks, setKiosks] = useState([]);
  const [events, setEvents] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  const [qrDialog, setQrDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formData, setFormData] = useState({
    name: '',
    eventId: '',
    location: '',
    customParams: ''
  });

  // API Functions
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [kiosksRes, eventsRes] = await Promise.all([
        axios.get('/api/kiosks', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/events', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setKiosks(kiosksRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return <CheckCircleIcon fontSize="small" />;
      case 'inactive': return <ErrorIcon fontSize="small" />;
      default: return <TabletMacIcon fontSize="small" />;
    }
  };

  const handleCreateKiosk = () => {
    setFormData({
      name: '',
      eventId: '',
      location: '',
      customParams: ''
    });
    setOpenDialog(true);
  };

  const handleSaveKiosk = async () => {
    if (!formData.name || !formData.eventId) {
      showSnackbar('Please fill in required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const event = events.find(e => e._id === formData.eventId || e.id === formData.eventId);
      const kioskData = {
        name: formData.name,
        url: `${window.location.origin}/kiosk?event=${formData.eventId}&${formData.customParams}`,
        eventId: formData.eventId,
        location: formData.location,
        status: 'Inactive'
      };

      await axios.post('/api/kiosks', kioskData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSnackbar('Kiosk created successfully');
      fetchData(); // Refresh data
      setOpenDialog(false);
    } catch (error) {
      console.error('Failed to create kiosk:', error);
      showSnackbar(
        error.response?.data?.error || 'Failed to create kiosk', 
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleLaunchKiosk = async (kiosk) => {
    try {
      const token = localStorage.getItem('token');
      
      // Update status to active
      await axios.patch(`/api/kiosks/${kiosk._id || kiosk.id}/status`, 
        { status: 'Active' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data to show updated status
      fetchData();
      
      // Open kiosk in new window/tab
      window.open(kiosk.url, '_blank', 'fullscreen=yes,menubar=no,toolbar=no,location=no,status=no');
      
      showSnackbar('Kiosk launched successfully');
    } catch (error) {
      console.error('Failed to launch kiosk:', error);
      showSnackbar('Failed to launch kiosk', 'error');
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      showSnackbar('URL copied to clipboard');
    }).catch(() => {
      showSnackbar('Failed to copy URL', 'error');
    });
  };

  const handleShowQR = (kiosk) => {
    setSelectedKiosk(kiosk);
    setQrDialog(true);
  };

  const formatLastUsed = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(date).toLocaleDateString();
  };

  const handleMenuClick = (event, kiosk) => {
    setAnchorEl(event.currentTarget);
    setSelectedKiosk(kiosk);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDeleteKiosk = async (kiosk) => {
  if (window.confirm(`Delete kiosk "${kiosk.name}"? This action cannot be undone.`)) {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/kiosks/${kiosk._id || kiosk.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Kiosk deleted successfully');
      fetchData(); // refresh list
    } catch (error) {
      console.error('Delete failed:', error);
      showSnackbar('Failed to delete kiosk', 'error');
    }
  }
};

  // Simple QR Code placeholder component
  const QRCodePlaceholder = ({ value }) => (
    <Box sx={{
      width: 200,
      height: 200,
      border: '2px solid #ddd',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      borderRadius: 2
    }}>
      <QRCode
      value={value || ''} 
      size={200} 
      includeMargin={true}
    />
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary" sx={{ mb: 1 }}>
            Kiosk Launcher
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and launch digital intake kiosks for events
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<TabletMacIcon />}
          onClick={handleCreateKiosk}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Create New Kiosk
        </Button>
      </Box>

      {/* Alert for instructions */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body2">
          <strong>How to use:</strong> Launch kiosks on tablets or computers at your event booth. 
          Visitors can fill out the intake form directly on the device.
        </Typography>
      </Alert>

      {/* Kiosks Grid */}
      {kiosks.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: 'center', p: 6 }}>
          <TabletMacIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No Kiosks Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first kiosk to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<TabletMacIcon />}
            onClick={handleCreateKiosk}
            sx={{ borderRadius: 2 }}
          >
            Create Kiosk
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {kiosks.map((kiosk) => (
            <Grid item xs={12} md={6} lg={4} key={kiosk._id || kiosk.id}>
              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                    <Avatar sx={{
                      bgcolor: getStatusColor(kiosk.status) === 'success' ? '#22c55e' : 
                               getStatusColor(kiosk.status) === 'error' ? '#f44336' : '#9e9e9e',
                      width: 48,
                      height: 48
                    }}>
                      {getStatusIcon(kiosk.status)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                        {kiosk.name}
                      </Typography>
                      <Chip
                        label={kiosk.status}
                        color={getStatusColor(kiosk.status)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    <IconButton
                      onClick={(e) => handleMenuClick(e, kiosk)}
                      sx="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Stats */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h5" fontWeight={700} color="primary">
                          {kiosk.submissionsToday || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Today's Submissions
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {formatLastUsed(kiosk.lastUsed)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last Used
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Location */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    📍 {kiosk.location || 'No location specified'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Actions */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<LaunchIcon />}
                      onClick={() => handleLaunchKiosk(kiosk)}
                      size="small"
                      sx={{ borderRadius: 2, flex: 1 }}
                    >
                      Launch
                    </Button>
                    <IconButton 
                      onClick={() => handleShowQR(kiosk)}
                      sx={{ 
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' }
                      }}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 150 }
        }}
      >
       <MenuItem 
         onClick={() => {
            handleCloseMenu();
            handleDeleteKiosk(selectedKiosk);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Kiosk
        </MenuItem>
      </Menu>

      {/* Create Kiosk Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight={700}>
            Create New Kiosk
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <TextField
              label="Kiosk Name *"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Toronto Expo - Booth A1"
              error={!formData.name}
              helperText={!formData.name ? 'Kiosk name is required' : ''}
            />
            
            <FormControl fullWidth error={!formData.eventId}>
              <InputLabel>Event *</InputLabel>
              <Select
                value={formData.eventId}
                label="Event *"
                onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
              >
                {events.map(event => (
                  <MenuItem key={event._id || event.id} value={event._id || event.id}>
                    {event.name}
                  </MenuItem>
                ))}
              </Select>
              {!formData.eventId && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  Please select an event
                </Typography>
              )}
            </FormControl>
            
            <TextField
              label="Location"
              fullWidth
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Convention Centre, Booth A1"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveKiosk}
            disabled={saving || !formData.name || !formData.eventId}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              'Create Kiosk'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog 
        open={qrDialog} 
        onClose={() => setQrDialog(false)}
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            QR Code for Kiosk
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedKiosk?.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <QRCodePlaceholder value={selectedKiosk?.url || ''} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Scan this QR code to access the kiosk directly
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => handleCopyUrl(selectedKiosk?.url)}
            startIcon={<ContentCopyIcon />}
            sx={{ borderRadius: 2 }}
          >
            Copy URL
          </Button>
          <Button onClick={() => setQrDialog(false)} sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
